/**
 * Global Monaco editor theme that matches the app's aurora glass palette.
 *
 * Importing this module for side-effects (e.g. once from main.tsx) registers
 * the theme with Monaco's loader and sets it as the default so every
 * `<Editor />` instance picks it up — callers should also set their `theme`
 * prop to the exported `AURORA_MONACO_THEME` constant.
 *
 * Palette reference (from tailwind + admin aurora styles):
 *   background    #0a0f1e  (rgb(var(--surface) / 1))
 *   panel darker  #040a16  (rgba(4, 10, 22, 1))
 *   text main     #d4eaf5
 *   text muted    #7d93a0
 *   accent blue   #3cb4e5  (brew-blue-light)
 *   brand blue    #007cba
 *   selection     #0f4a70
 *   warn          #f0c674 / #fbbf24
 *   danger        #f5a8a8 / #f87171
 *   success       #7de0a7 / #4ade80
 *   purple(ish)   #c8a6f5
 */
import { loader, type BeforeMount } from '@monaco-editor/react';
import type * as Monaco from 'monaco-editor';

// Point the Monaco AMD loader at the locally-served bundle (copied into
// dist/monaco/vs at build time, served from node_modules in dev). Without
// this, @monaco-editor/loader fetches Monaco from cdn.jsdelivr.net, which
// breaks airgap deployments.
loader.config({ paths: { vs: '/monaco/vs' } });

export const AURORA_MONACO_THEME = 'aurora-dark';

const THEME_DATA: Monaco.editor.IStandaloneThemeData = {
  base: 'vs-dark',
  inherit: true,
  rules: [
    { token: '', foreground: 'd4eaf5', background: '0a0f1e' },
    { token: 'comment', foreground: '6b8a9e', fontStyle: 'italic' },
    { token: 'string', foreground: 'd4eaf5' },
    { token: 'string.key.json', foreground: '7cc8e8', fontStyle: 'bold' },
    { token: 'string.value.json', foreground: 'd4eaf5' },
    { token: 'number', foreground: 'f0c674' },
    { token: 'number.json', foreground: 'f0c674' },
    { token: 'keyword', foreground: 'c8a6f5' },
    { token: 'keyword.json', foreground: 'c8a6f5' },
    { token: 'delimiter', foreground: '7d93a0' },
    { token: 'delimiter.bracket.json', foreground: '7cc8e8' },
    { token: 'delimiter.array.json', foreground: '7cc8e8' },
    { token: 'delimiter.colon.json', foreground: '7d93a0' },
    { token: 'delimiter.comma.json', foreground: '7d93a0' },
    { token: 'invalid', foreground: 'f87171' },
  ],
  colors: {
    'editor.background': '#0a0f1e',
    'editor.foreground': '#d4eaf5',
    'editorLineNumber.foreground': '#3a5a75',
    'editorLineNumber.activeForeground': '#7cc8e8',
    'editorCursor.foreground': '#3cb4e5',
    'editor.selectionBackground': '#0f4a70',
    'editor.selectionHighlightBackground': '#3cb4e533',
    'editor.inactiveSelectionBackground': '#0a3050',
    'editor.wordHighlightBackground': '#3cb4e522',
    'editor.wordHighlightStrongBackground': '#3cb4e544',
    'editor.lineHighlightBackground': '#0f1a2e',
    'editor.lineHighlightBorder': '#0f1a2e',
    'editor.findMatchBackground': '#3cb4e555',
    'editor.findMatchHighlightBackground': '#3cb4e522',
    'editor.rangeHighlightBackground': '#3cb4e51a',
    'editorIndentGuide.background': '#152035',
    'editorIndentGuide.activeBackground': '#2a4358',
    'editorWhitespace.foreground': '#1c2b45',
    'editorBracketMatch.background': '#0f4a7055',
    'editorBracketMatch.border': '#3cb4e5',
    'editorGutter.background': '#0a0f1e',
    'editorGutter.modifiedBackground': '#3cb4e5',
    'editorGutter.addedBackground': '#4ade80',
    'editorGutter.deletedBackground': '#f87171',
    'editorError.foreground': '#f87171',
    'editorWarning.foreground': '#fbbf24',
    'editorInfo.foreground': '#3cb4e5',
    'editorHint.foreground': '#7cc8e8',
    'editorRuler.foreground': '#152035',

    // Scrollbar
    'scrollbar.shadow-sm': '#00000000',
    'scrollbarSlider.background': '#2a435899',
    'scrollbarSlider.hoverBackground': '#3cb4e555',
    'scrollbarSlider.activeBackground': '#3cb4e577',

    // Widgets (suggestion, hover, parameter hints)
    'editorWidget.background': '#0c1626',
    'editorWidget.border': '#1d3a52',
    'editorWidget.foreground': '#d4eaf5',
    'editorHoverWidget.background': '#0c1626',
    'editorHoverWidget.border': '#1d3a52',
    'editorHoverWidget.foreground': '#d4eaf5',
    'editorSuggestWidget.background': '#0c1626',
    'editorSuggestWidget.border': '#1d3a52',
    'editorSuggestWidget.foreground': '#d4eaf5',
    'editorSuggestWidget.highlightForeground': '#3cb4e5',
    'editorSuggestWidget.selectedBackground': '#0f3a5c',
    'editorSuggestWidget.selectedForeground': '#ffffff',
    'editorSuggestWidget.focusHighlightForeground': '#7cc8e8',

    // Inputs used by find widget etc.
    'input.background': '#040a16',
    'input.foreground': '#d4eaf5',
    'input.border': '#1d3a52',
    'inputOption.activeBorder': '#3cb4e5',
    'inputValidation.errorBorder': '#f87171',
    'inputValidation.warningBorder': '#fbbf24',
    'inputValidation.infoBorder': '#3cb4e5',

    // Misc
    focusBorder: '#3cb4e566',
    'list.hoverBackground': '#0f3a5c66',
    'list.activeSelectionBackground': '#0f3a5c',
    'list.activeSelectionForeground': '#ffffff',
    'list.focusBackground': '#0f3a5c',
    'list.focusForeground': '#ffffff',
  },
};

/**
 * Register the theme once with the Monaco loader (applies to every Editor
 * created after init resolves). Idempotent across modules.
 */
let initPromise: Promise<void> | null = null;
export function ensureAuroraMonacoTheme(): Promise<void> {
  initPromise ??= loader
    .init()
    .then((monaco) => {
      monaco.editor.defineTheme(AURORA_MONACO_THEME, THEME_DATA);
      monaco.editor.setTheme(AURORA_MONACO_THEME);
    })
    .catch(() => {
      // Loader init can fail in unusual environments (e.g. offline); fall
      // back to the built-in dark theme silently.
      initPromise = null;
    });
  return initPromise;
}

/**
 * Editor `beforeMount` handler that guarantees the theme is defined before
 * the editor renders — useful when the component mounts before the loader's
 * init promise resolves. Safe to use on every `<Editor />`.
 */
export const registerAuroraMonacoTheme: BeforeMount = (monaco) => {
  monaco.editor.defineTheme(AURORA_MONACO_THEME, THEME_DATA);
};

// Kick off registration eagerly on import.
// fire-and-forget: ensureAuroraMonacoTheme() carries a .catch, so it cannot reject
void ensureAuroraMonacoTheme();
